'use client'

import { useMemo } from 'react'
import useCollectionInscriptions from '@/utils/hooks/useCollectionInscriptions'
import useCollectionRarity from '@/utils/hooks/useCollectionRarity'
import useEscrows from '@/utils/hooks/useEscrows'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import { monkeFilterUrl, relatedMonkes, type TraitItem } from '@/utils/nodemonkes'
import InscriptionContent from '@/components/inscription-content'

export default function MonkeTraitsAndRelated({ inscription, navigate, showRelated = true }: { inscription: TraitItem; navigate: (url: string) => void; showRelated?: boolean }) {
	const { data: items } = useCollectionInscriptions('nodemonkes')
	const { data: rarity } = useCollectionRarity('nodemonkes')
	const { data: escrows } = useEscrows('nodemonkes')
	const open = useInscriptionModal()
	const listingMap = useMemo(() => new Map<string, number>((Array.isArray(escrows) ? escrows : []).filter((e) => e.satoshi_price > 0).map((e) => [e.inscription_id, e.satoshi_price])), [escrows])
	const related = useMemo(() => relatedMonkes(inscription, Array.isArray(items) ? items : [], new Set(listingMap.keys())), [inscription, items, listingMap])
	const traitMarkets = useMemo(() => {
		const result = new Map<string, { count: number; floor: number }>()
		for (const item of (Array.isArray(items) ? items : []) as TraitItem[]) {
			const price = listingMap.get(item.id)
			if (!price) continue
			for (const attr of item.meta?.attributes || []) {
				const key = `${attr.trait_type}:${attr.value}`
				const current = result.get(key)
				result.set(key, { count: (current?.count || 0) + 1, floor: Math.min(current?.floor || Infinity, price) })
			}
		}
		return result
	}, [items, listingMap])
	return <div className="space-y-6 p-4 sm:p-5 md:p-6">
		<section aria-label="Monke traits"><h3 className="mb-3 text-[11px] font-semibold text-muted-foreground">Traits & scarcity</h3>
			<div className="grid grid-cols-2 gap-2">{(inscription.meta?.attributes || []).map((attr) => {
				const count = rarity?.map[attr.trait_type.toLowerCase()]?.[String(attr.value).toLowerCase()]
				const market = traitMarkets.get(`${attr.trait_type}:${attr.value}`)
				return <button type="button" key={attr.trait_type} onClick={() => navigate(monkeFilterUrl([{ trait_type: attr.trait_type, values: [String(attr.value)] }]))} className="min-w-0 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary focus-visible:outline-primary">
					<span className="block text-[10px] text-muted-foreground">{attr.trait_type === 'Count' ? 'Trait count' : attr.trait_type}</span>
					<span className="mt-1 block break-words text-sm font-semibold">{String(attr.value)}</span>
					{count != null && rarity?.totalSupply && <span className="mt-1 block text-xs text-muted-foreground">{count.toLocaleString()} of {rarity.totalSupply.toLocaleString()} · {(count / rarity.totalSupply * 100).toFixed(2)}%</span>}
					{Array.isArray(items) && Array.isArray(escrows) && <span className="mt-2 block text-[11px] text-muted-foreground">{market ? `${market.count} on OW · from ${(market.floor / 1e8).toFixed(4)} BTC` : 'None listed on OW'}</span>}
				</button>
			})}</div>
		</section>
		{showRelated && related.length > 0 && <section aria-label="Related Monkes"><h3 className="mb-3 text-[11px] font-semibold text-muted-foreground">Similar Monkes</h3><div className="grid grid-cols-3 gap-2">{related.map(({ item, matches }) => {
			const full = item as TraitItem & { meta?: { name?: string } }
			const price = listingMap.get(item.id)
			return <button type="button" key={item.id} onClick={() => open({ id: item.id, inscription: { ...item, collection: { slug: 'nodemonkes', name: 'NodeMonkes' } } })} className="min-w-0 overflow-hidden rounded-lg border border-border text-left hover:border-primary">
				<div className="aspect-square bg-muted/30"><InscriptionContent inscription={item} className="size-full object-contain" /></div>
				<div className="space-y-0.5 p-2"><span className="block break-words text-xs font-semibold">{full.meta?.name || 'NodeMonke'}</span><span className="block text-[10px] text-muted-foreground">{matches} shared {matches === 1 ? 'trait' : 'traits'}</span>{price && <span className="block text-[10px] font-medium">{(price / 1e8).toFixed(4)} BTC</span>}</div>
			</button>
		})}</div></section>}
	</div>
}
